import {
  positionPopover,
  computeArrowOffset,
  type Alignment,
  type Placement,
  type Offset,
} from '@repo/dom/position';

import './floatingSurface.css';

export interface FloatingPositionConfig {
  floating: HTMLElement;
  trigger: { getBoundingClientRect(): DOMRect };
  arrow: HTMLSpanElement | null;
  placement: Placement;
  align: Alignment;
  offset: Offset;
  viewportMargin: number;
  hasArrow: boolean;
}

export const availableHeightProperty = '--n-floating-available-height';
export const availableWidthProperty = '--n-floating-available-width';

/**
 * Lifts a size cap published on `element` so it can be measured at its
 * natural size. `initial` rather than removing the inline value: for a custom
 * property it is the guaranteed-invalid value, so `var()` takes its fallback,
 * whereas with no value at all a surface nested in another positioned one
 * inherits the outer surface's cap and would be measured under it.
 */
const resetAvailableSpace = (element: HTMLElement) => {
  element.style.setProperty(availableHeightProperty, 'initial');
  element.style.setProperty(availableWidthProperty, 'initial');
};

/**
 * Lifting the cap and clearing the overflow flags turns the surface's scroller
 * back into a plain box for the measurement, which drops its scroll position. Returns a restore for every
 * element that had one, which writes only where the value was lost, so a
 * scroll this pass never disturbed (a listbox's own list) is left mid-flight.
 */
const saveScrollPositions = (root: HTMLElement) => {
  const saved = [root, ...root.querySelectorAll<HTMLElement>('*')]
    .filter((element) => element.scrollTop > 0 || element.scrollLeft > 0)
    .map((element) => ({
      element,
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop,
    }));

  return () => {
    for (const { element, scrollLeft, scrollTop } of saved) {
      if (element.scrollTop !== scrollTop) element.scrollTop = scrollTop;
      if (element.scrollLeft !== scrollLeft) element.scrollLeft = scrollLeft;
    }
  };
};

/**
 * The one element inside a surface's wrapper, which the `Tooltip` and
 * `Popover` modules let take the cap and scroll itself (see their lone-child
 * rules). Found by shape rather than class, so the other roots this positions
 * get a flag on their lone grandchild too, which nothing there styles. Not
 * looked for under an arrow: an arrowed surface is not capped, and its arrow
 * hangs outside the child, so the child would always read as overflowing.
 */
const getLoneChild = (floating: HTMLElement) => {
  const wrapper = floating.firstElementChild;
  if (wrapper?.childElementCount !== 1) return null;
  const child = wrapper.firstElementChild;
  return child instanceof HTMLElement ? child : null;
};

/** Returns the side the surface landed on, after any flip. */
export const applyFloatingPosition = (
  config: FloatingPositionConfig,
): Placement => {
  const {
    floating,
    trigger,
    arrow,
    placement,
    align,
    offset,
    viewportMargin,
    hasArrow,
  } = config;

  const triggerRect = trigger.getBoundingClientRect();
  const restoreScrollPositions = saveScrollPositions(floating);

  floating.dataset.align = align;
  floating.dataset.placement = placement;
  floating.dataset.floatingSurface = '';

  // The side is chosen from the box's natural size, so neither a cap an
  // earlier call published nor the scroller it switched on may shape it.
  resetAvailableSpace(floating);
  // Nor where the last pass put it. The UA `[popover]` inset leaves `right: 0`,
  // so the root's fit-content width is capped by the room right of its `left`:
  // read there, content that widened since measures short, lands too far
  // right, and every frame's pass edges it a few pixels further. Both reads
  // below are taken here, before the final position is written, and in the
  // same task, so the origin is never painted.
  floating.style.left = '0px';
  floating.style.top = '0px';
  delete floating.dataset.overflowing;
  const loneChild = hasArrow ? null : getLoneChild(floating);
  if (loneChild) delete loneChild.dataset.overflowing;

  const { flip, available } = positionPopover(
    triggerRect,
    floating.getBoundingClientRect(),
    placement,
    align,
    { offset, viewportMargin },
  );

  const finalPlacement = flip.placement ?? placement;
  floating.dataset.placement = finalPlacement;

  floating.style.setProperty(availableHeightProperty, `${available.height}px`);
  floating.style.setProperty(availableWidthProperty, `${available.width}px`);

  // A lone child is stretched to the capped wrapper's line whether or not it
  // scrolls, so its box is already the capped one and its scroll size is its
  // content. Flagged before the root is read: a child that overflows becomes
  // a scroller and contains what it holds, whereas a plain box its overflow
  // would count as the root's own and read as a spill.
  if (loneChild) {
    loneChild.dataset.overflowing = String(
      loneChild.scrollHeight > loneChild.clientHeight ||
        loneChild.scrollWidth > loneChild.clientWidth,
    );
  }

  // The read lays the box out under the cap just published, with the root's
  // scroller off and every rule keyed on the flag in its unset state, so it
  // counts only what could not shrink into the room and spilled out. Content
  // that takes the cap and scrolls itself — a lone `Tooltip`/`Popover` child,
  // a listbox, a menu list — never spills, so its root reads false. Only for
  // true does `floatingSurface.css` switch the root's `overflow: auto` on,
  // because a scroll container clips its children's shadows and focus rings
  // even when nothing scrolls. Set before the second measurement, so that
  // measures the box as it will render.
  floating.dataset.overflowing = String(
    floating.scrollHeight > floating.clientHeight,
  );

  // Placed again from the capped box: a `top` or `left` surface hangs off its
  // far edge, so one that shrank would otherwise sit short of its trigger. The
  // side is locked because the width cap can wrap text and make the box
  // taller than it measured the first time, and a flip here would leave
  // `data-placement` and the height cap describing the other side.
  const floatingRect = floating.getBoundingClientRect();
  const { position } = positionPopover(
    triggerRect,
    floatingRect,
    finalPlacement,
    align,
    { offset, viewportMargin, lockPlacement: true },
  );

  floating.style.top = `${position.y}px`;
  floating.style.left = `${position.x}px`;

  restoreScrollPositions();

  if (!hasArrow || !arrow) return finalPlacement;

  const arrowOffset = computeArrowOffset({
    triggerRect,
    popoverRect: floatingRect,
    position,
    finalPlacement,
  });

  arrow.style.left = '';
  arrow.style.top = '';

  if (finalPlacement === 'top' || finalPlacement === 'bottom') {
    arrow.style.left = `${arrowOffset}px`;
  } else {
    arrow.style.top = `${arrowOffset}px`;
  }

  return finalPlacement;
};
